import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function ITSkillsSection({ profile }) {
  const queryClient = useQueryClient();
  const skills = profile.it_skills || [];

  const [isOpen, setIsOpen] = useState(false);

  const [formData, setFormData] = useState({
    skill: "",
    version: "",
    experience: "",
    last_used: "",
    proficiency_level: "Intermediate",
  });

  // MUTATIONS
  const saveMutation = useMutation({
    mutationFn: (data) => backendApi.entities.UserProfile.addITSkill(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteITSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      skill: "",
      version: "",
      experience: "",
      last_used: "",
      proficiency_level: "Intermediate",
    });
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this IT skill?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">IT skills</h3>

        <button
          onClick={() => setIsOpen(true)}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {(profile?.it_skills && profile.it_skills.length > 0) ? "Edit" : "Add"}
        </button>
      </div>

      {/* SKILLS LIST */}
      {skills.length > 0 ? (
        <div className="space-y-3">
          {skills.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
            >
              <div>
                <p className="font-medium text-gray-800">{item.skill}</p>
                <p className="text-sm text-gray-500">
                  {item.version && `v${item.version} | `}
                  {item.experience && `${item.experience} experience | `}
                  {item.proficiency_level && `${item.proficiency_level} | `}
                  {item.last_used && `Last used: ${item.last_used}`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Pencil className="w-4 h-4 text-gray-400 cursor-pointer hover:text-blue-600" />
                <Trash2 
                  className="w-4 h-4 text-gray-400 cursor-pointer hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" 
                  onClick={() => handleDelete(item.id)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          Add your IT / technical skills to make your profile strong.
        </p>
      )}

      {/* ADD DIALOG */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add IT Skill</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Skill */}
            <div>
              <Label>Skill / Software Name</Label>
              <Input
                value={formData.skill}
                onChange={(e) =>
                  setFormData({ ...formData, skill: e.target.value })
                }
                placeholder="e.g. React, Python, AWS"
              />
            </div>

            {/* Version */}
            <div>
              <Label>Version (optional)</Label>
              <Input
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="e.g. 18.2"
              />
            </div>

            {/* Experience */}
            <div>
              <Label>Experience</Label>
              <Input
                value={formData.experience}
                onChange={(e) =>
                  setFormData({ ...formData, experience: e.target.value })
                }
                placeholder="e.g. 2 Years"
              />
            </div>

            {/* Last Used */}
            <div>
              <Label>Last Used</Label>
              <Input
                type="month"
                value={formData.last_used}
                onChange={(e) =>
                  setFormData({ ...formData, last_used: e.target.value })
                }
              />
            </div>

            {/* Proficiency Level */}
            <div>
              <Label>Proficiency Level</Label>
              <select
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.proficiency_level}
                onChange={(e) => setFormData({ ...formData, proficiency_level: e.target.value })}
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
